import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CheckCircle2, ShieldAlert, Loader2 } from "lucide-react";

// Public bounce page hit from a push notification tap.
// GET /script-ack/:token → POST the token → success flash → jump to
// the exact change row on the project dashboard.
export default function ScriptAckLanding() {
  const { token } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState("loading"); // loading | ok | expired | duplicate
  const [gate, setGate]   = useState({ acked_required: 0, required_count: 0 });
  const [errMsg, setErrMsg] = useState("");
  const [projectId, setProjectId] = useState("");
  const [changeId, setChangeId]   = useState("");
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current || !token) return;
    fired.current = true;

    const call = async () => {
      try {
        const { data } = await api.post("/script-changes/ack-by-token",
                                        { token });
        setProjectId(data.project_id || "");
        setChangeId(data.change_id || "");
        setGate({
          acked_required: data.acked_required || 0,
          required_count: data.required_count || 0,
        });
        setState(data.already_acked ? "duplicate" : "ok");
      } catch (err) {
        setState("expired");
        setErrMsg(formatApiErrorDetail(err.response?.data?.detail)
                   || "This ack link is invalid or has expired.");
      }
    };
    void call();
  }, [token]);

  // On success/duplicate, jump to the row after 1.4 s.
  useEffect(() => {
    if (state !== "ok" && state !== "duplicate") return;
    const t = setTimeout(() => {
      if (projectId && changeId) {
        nav(`/p/${projectId}#script-change-row-${changeId}`,
            { replace: true });
      } else if (projectId) {
        nav(`/p/${projectId}`, { replace: true });
      } else {
        nav("/projects", { replace: true });
      }
    }, 1400);
    return () => clearTimeout(t);
  }, [state, projectId, changeId, nav]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4"
         data-testid="script-ack-landing">
      <div className="cs-card rounded-2xl p-8 max-w-md w-full text-center">
        {state === "loading" && (
          <>
            <Loader2 size={36} className="mx-auto text-white/70 animate-spin mb-4" />
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55 mb-2">
              Verifying ack link
            </div>
            <div className="font-display text-2xl uppercase tracking-tight">
              One moment…
            </div>
          </>
        )}

        {state === "ok" && (
          <div data-testid="script-ack-success">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={30} className="text-emerald-300" />
            </div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-300 mb-2">
              Acknowledged
            </div>
            <div className="font-display text-2xl uppercase tracking-tight">
              Shoot cleared on your end
            </div>
            <div className="mt-3 text-sm text-white/60">
              {gate.required_count > 0
                ? `${gate.acked_required} of ${gate.required_count} HOD receipts in.`
                : "Receipt saved."}
            </div>
            <div className="mt-4 text-[10px] tracking-[0.22em] uppercase text-white/40">
              Opening the change…
            </div>
          </div>
        )}

        {state === "duplicate" && (
          <div data-testid="script-ack-duplicate">
            <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={30} className="text-white/70" />
            </div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55 mb-2">
              Already acknowledged
            </div>
            <div className="font-display text-2xl uppercase tracking-tight">
              You&apos;re all set
            </div>
            <div className="mt-3 text-sm text-white/60">
              This receipt was already stamped. Opening the change so you can review.
            </div>
          </div>
        )}

        {state === "expired" && (
          <div data-testid="script-ack-expired">
            <div className="w-16 h-16 rounded-full bg-red-500/15 flex items-center justify-center mx-auto mb-4">
              <ShieldAlert size={30} className="text-red-300" />
            </div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-red-300 mb-2">
              Link expired
            </div>
            <div className="font-display text-2xl uppercase tracking-tight">
              Please ack manually
            </div>
            <div className="mt-3 text-sm text-white/60">
              {errMsg}
            </div>
            <button
              onClick={() => nav("/projects", { replace: true })}
              className="mt-6 h-11 px-5 rounded-full text-xs uppercase tracking-[0.22em] border border-white/20 hover:border-white/40 transition-colors"
              data-testid="script-ack-manual-cta"
            >
              Open my projects
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
